import { Button, Popup as PopupOld } from '@approvalmax/ui';
import { Form, Popup, useConfirmation } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { BeneficiaryStatusLegend } from 'modules/request/components/xero/BeneficiaryStatusLegend/BeneficiaryStatusLegend';
import { useProgressLoading } from 'modules/utils/hooks/useProgressLoading';
import { getAirwallexContext } from 'pages/requestForm/selectors/airwallex/airwallexSelectors';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle, useUnmount } from 'react-use';
import { FeatureBadge } from 'shared/components';
import { AddBillInvoicesPopup } from 'shared/components';
import { useFilters } from 'shared/components/AddBillInvoicesPopup/AddBillInvoicesPopup.hooks';
import { useCheckAirwallexBeneficiaries } from 'shared/data/webApp/v1';
import { useUserBetaFeatures } from 'shared/hooks';

import { updateXeroAirwallexBPDetails } from '../../../../../actions';
import { RequesterInstructionSection } from '../../../../../components';
import ActionsBlock from '../../../../form/ActionsBlock/ActionsBlock';
import { useXeroAirwallexBatchPaymentFormContext } from '../../XeroAirwallexBatchPaymentForm.context';
import { FilterTrasferData } from '../../XeroAirwallexBatchPaymentForm.types';
import { AddBeneficiaryPopupData, CheckBeneficiaryResult } from '../../XeroAirwallexBatchPaymentForm.types';
import BillsInformation from '../BillsInformation/BillsInformation';
import AddBeneficiaryPopup from '../BillsInformation/components/AddBeneficiaryPopup/AddBeneficiaryPopup';
import { AddBeneficiaryPopupContent } from '../BillsInformation/components/AddBeneficiaryPopupContent/AddBeneficiaryPopupContent';
import ChangedBeneficiariesPopup from '../ChangedBeneficiariesPopup/ChangedBeneficiariesPopup';
import { ChangedBeneficiariesPopupContent } from '../ChangedBeneficiariesPopupContent/ChangedBeneficiariesPopupContent';
import DeletedBeneficiariesPopup from '../DeletedBeneficiariesPopup/DeletedBeneficiariesPopup';
import { DeletedBeneficiariesPopupContent } from '../DeletedBeneficiariesPopupContent/DeletedBeneficiariesPopupContent';
import { confirmationPopupProps, popupTargetElementId } from './XeroAirwallexBatchPaymentFormContent.constants';
import { mapFilterTransfer } from './XeroAirwallexBatchPaymentFormContent.helpers';
import { useBillsForBatchPaymentData } from './XeroAirwallexBatchPaymentFormContent.hooks';
import { messages } from './XeroAirwallexBatchPaymentFormContent.messages';
import {
    BatchPaymentBills,
    LegendContainer,
    Request,
    Root,
    StyledLoadingBar,
    Subheader,
    SubheaderButton,
    SubheaderButtonsWrapper,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderText,
    ToolbarHeaderTextContainer,
} from './XeroAirwallexBatchPaymentFormContent.styles';
import { XeroAirwallexBatchPaymentFormProps } from './XeroAirwallexBatchPaymentFormContent.types';

const XeroAirwallexBatchPaymentFormContent = memo<XeroAirwallexBatchPaymentFormProps>((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const airwallexContext = useSelector(getAirwallexContext);
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, request.companyId));
    const showPremiumBadge = company.flags.isAllFeaturesTrialActive || company.flags.isTrialLicense;
    const [isOpenAddBillsPopup, toggleIsOpenAddBillsPopup] = useToggle(false);
    const [addBeneficiaryPopupData, setAddBeneficiaryPopupData] = useState<AddBeneficiaryPopupData>({ isOpen: false });
    const {
        setCheckBeneficiaryResultsByInvoiceId,
        clearItems,
        setShowErrors,
        saveAndSubmit,
        isSaving,
        billBatchPayment,
        updateAirwallexBillBatchPayment,
        deletedBeneficiariesData,
        changedBeneficiariesData,
        setDeletedBeneficiariesData,
        setChangedBeneficiariesData,
        applyChangedBeneficiaries,
        addBills,
        editMode,
    } = useXeroAirwallexBatchPaymentFormContext();
    const { mutate: checkAirwallexBeneficiaries, isLoading: isLoadingCheckAirwallexBeneficiaries } =
        useCheckAirwallexBeneficiaries();
    const {
        isBelgradeUIDeletedBeneficiariesPopup,
        isBelgradeUIAddBeneficiaryPopup,
        isBelgradeUIChangedBeneficiariesPopup,
    } = useUserBetaFeatures();

    const { confirmation } = useConfirmation(confirmationPopupProps);

    const {
        initialTransferFilterData,
        sortingConfig,
        filterTransfer,
        setFilterTransfer,
        changeSortingConfig,
        applyFilters,
        resetFilter,
        form: filtersForm,
    } = useFilters<FilterTrasferData>(mapFilterTransfer);

    const { billsForBatchPaymentData, billsForBatchPaymentLoading } = useBillsForBatchPaymentData({
        requestId: request.id,
        companyId: request.companyId,
        companyCurrency: request.companyCurrency,
        filterTransfer,
        enabled: isOpenAddBillsPopup,
    });

    const disableCheckBeneficiaries = useMemo(
        () => billBatchPayment.items.every((item) => !item.beneficiary),
        [billBatchPayment.items]
    );

    const handleSubmitFilters = filtersForm.handleSubmit(applyFilters);

    const handleAddBills = useCallback(
        (selectableBills: domain.AirwallexBatchSelectableBillItem[]) => {
            addBills(selectableBills);
            filtersForm.reset();
            setFilterTransfer(initialTransferFilterData);
            toggleIsOpenAddBillsPopup(false);
        },
        [addBills, filtersForm, initialTransferFilterData, setFilterTransfer, toggleIsOpenAddBillsPopup]
    );

    const checkBeneficiaries = useCallback(() => {
        const beneficiariesToCheck = billBatchPayment.items.reduce((accum, item) => {
            if (!item.contact || !item.beneficiary) return accum;

            return [
                ...accum,
                {
                    contactId: item.contact.id,
                    currency: item.currency,
                    airwallexBeneficiaryId: item.beneficiary.id,
                    airwallexBeneficiaryVersion: item.beneficiary.version,
                },
            ];
        }, []);

        checkAirwallexBeneficiaries(
            {
                body: {
                    companyId: company.id,
                    beneficiariesToCheck,
                },
            },
            {
                onSuccess: (response) => {
                    const result = billBatchPayment.items.reduce<Record<string, CheckBeneficiaryResult>>(
                        (accum, item) => {
                            const itemContact = item.contact;
                            const itemBeneficiary = item.beneficiary;

                            if (!itemContact || !itemBeneficiary) return accum;

                            const beneficiaryCheckResult = response.checkResults.find(
                                (checkResult) =>
                                    checkResult.contactId === itemContact.id &&
                                    checkResult.currency === item.currency &&
                                    itemBeneficiary.id === checkResult.beneficiaryDiscrepancy.airwallexBeneficiaryId
                            );

                            if (!beneficiaryCheckResult) return accum;

                            return {
                                ...accum,
                                [item.xeroBillInvoiceRequestId]: {
                                    status: beneficiaryCheckResult.beneficiaryPaymentStatus,
                                    beneficiaryDiscrepancy: beneficiaryCheckResult.beneficiaryDiscrepancy,
                                },
                            };
                        },
                        {}
                    );

                    setCheckBeneficiaryResultsByInvoiceId(result);
                },
            }
        );
    }, [billBatchPayment.items, checkAirwallexBeneficiaries, company.id, setCheckBeneficiaryResultsByInvoiceId]);

    const closeAddBeneficiaryPopup = useCallback(() => {
        setAddBeneficiaryPopupData({ isOpen: false });
    }, []);

    useProgressLoading(isSaving);

    useEffect(() => {
        updateAirwallexBillBatchPayment(request.details);
    }, [request.details, updateAirwallexBillBatchPayment]);

    useUnmount(() => {
        dispatch(updateXeroAirwallexBPDetails(billBatchPayment));
    });

    const onSaveAndSubmit = useCallback(
        (commentText?: string) => {
            setShowErrors(true);
            saveAndSubmit(commentText);
        },
        [saveAndSubmit, setShowErrors]
    );

    const closeDeletedBeneficiariesPopup = useCallback(() => {
        setDeletedBeneficiariesData(null);
    }, [setDeletedBeneficiariesData]);

    const closeChangedBeneficiariesPopup = useCallback(() => {
        setChangedBeneficiariesData(null);
    }, [setChangedBeneficiariesData]);

    const handleCloseAddBeneficiaryPopup = useCallback(() => {
        confirmation()
            .then(() => {
                closeAddBeneficiaryPopup();
            })
            .catch(() => {});
    }, [confirmation, closeAddBeneficiaryPopup]);

    return (
        <>
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderTextContainer>
                            <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                            {showPremiumBadge && <FeatureBadge premium />}
                        </ToolbarHeaderTextContainer>

                        <ActionsBlock
                            customSaveAndSubmitRequest={onSaveAndSubmit}
                            isSaving={isSaving || isLoadingCheckAirwallexBeneficiaries}
                        />
                    </ToolbarHeader>

                    <StyledLoadingBar $hide={!isLoadingCheckAirwallexBeneficiaries} />
                </Toolbar>

                <Request>
                    <RequesterInstructionSection instructionText={airwallexContext.requesterInstruction} />

                    <Subheader>
                        <BatchPaymentBills>
                            {messages.batchPaymentBillsQty({ qty: billBatchPayment.items.length })}
                        </BatchPaymentBills>

                        <SubheaderButtonsWrapper>
                            <SubheaderButton
                                colorTheme='black'
                                execute={checkBeneficiaries}
                                disabled={disableCheckBeneficiaries}
                            >
                                {messages.checkBeneficiaries}
                            </SubheaderButton>

                            <SubheaderButton
                                colorTheme='red'
                                execute={clearItems}
                                disabled={
                                    isLoadingCheckAirwallexBeneficiaries ||
                                    billBatchPayment.items.length === 0 ||
                                    editMode === RequestEditMode.Approver
                                }
                            >
                                {messages.removeAllBills}
                            </SubheaderButton>

                            <Button
                                execute={toggleIsOpenAddBillsPopup}
                                disabled={isLoadingCheckAirwallexBeneficiaries || editMode === RequestEditMode.Approver}
                            >
                                {messages.addBills}
                            </Button>
                        </SubheaderButtonsWrapper>
                    </Subheader>

                    <LegendContainer>
                        <BeneficiaryStatusLegend redText={messages.bankDetailsChanged} />
                    </LegendContainer>

                    <BillsInformation request={request} setAddBeneficiaryPopupData={setAddBeneficiaryPopupData} />
                </Request>
            </Root>

            <Popup open={isOpenAddBillsPopup} onToggle={toggleIsOpenAddBillsPopup} size='large' qa='add-bill-invoices'>
                <Form form={filtersForm} onSubmit={handleSubmitFilters}>
                    <AddBillInvoicesPopup
                        billBatchPayment={billBatchPayment}
                        companyId={request.companyId}
                        requestId={request.id}
                        data={billsForBatchPaymentData}
                        isLoading={billsForBatchPaymentLoading}
                        resetFilter={resetFilter}
                        sortingConfig={sortingConfig}
                        changeSortingConfig={changeSortingConfig}
                        handleAddBills={handleAddBills}
                        attentionText={messages.addBillsAttentionText}
                    />
                </Form>
            </Popup>

            {isBelgradeUIAddBeneficiaryPopup ? (
                <Popup open={addBeneficiaryPopupData.isOpen} onClose={handleCloseAddBeneficiaryPopup}>
                    <AddBeneficiaryPopupContent
                        closePopup={closeAddBeneficiaryPopup}
                        initialCurrency={addBeneficiaryPopupData.initialBillCurrency}
                        initialBillId={addBeneficiaryPopupData.initialBillId}
                    />
                </Popup>
            ) : (
                <PopupOld
                    isOpen={addBeneficiaryPopupData.isOpen}
                    onRequestClose={closeAddBeneficiaryPopup}
                    targetElementId={popupTargetElementId}
                    disableAutoClose
                >
                    <AddBeneficiaryPopup
                        closePopup={closeAddBeneficiaryPopup}
                        initialCurrency={addBeneficiaryPopupData.initialBillCurrency}
                        initialBillId={addBeneficiaryPopupData.initialBillId}
                    />
                </PopupOld>
            )}

            {isBelgradeUIDeletedBeneficiariesPopup ? (
                <Popup open={Boolean(deletedBeneficiariesData)} onClose={closeDeletedBeneficiariesPopup}>
                    <DeletedBeneficiariesPopupContent
                        onClose={closeDeletedBeneficiariesPopup}
                        requestName={request.displayName}
                        deletedBeneficiaries={deletedBeneficiariesData || []}
                    />
                </Popup>
            ) : (
                <PopupOld
                    isOpen={Boolean(deletedBeneficiariesData)}
                    onRequestClose={closeDeletedBeneficiariesPopup}
                    targetElementId={popupTargetElementId}
                    disableAutoClose
                >
                    <DeletedBeneficiariesPopup
                        onClose={closeDeletedBeneficiariesPopup}
                        title={request.displayName}
                        deletedBeneficiaries={deletedBeneficiariesData || []}
                    />
                </PopupOld>
            )}

            {isBelgradeUIChangedBeneficiariesPopup ? (
                <Popup open={Boolean(changedBeneficiariesData)} onClose={closeChangedBeneficiariesPopup}>
                    <ChangedBeneficiariesPopupContent
                        subTitle={request.displayName}
                        beneficiaryDiscrepancies={changedBeneficiariesData || []}
                        onApply={applyChangedBeneficiaries}
                        onClose={closeChangedBeneficiariesPopup}
                    />
                </Popup>
            ) : (
                <PopupOld
                    isOpen={Boolean(changedBeneficiariesData)}
                    onRequestClose={closeChangedBeneficiariesPopup}
                    targetElementId={popupTargetElementId}
                    disableAutoClose
                >
                    <ChangedBeneficiariesPopup
                        title={request.displayName}
                        onApply={applyChangedBeneficiaries}
                        beneficiaryDiscrepancies={changedBeneficiariesData || []}
                        onClose={closeChangedBeneficiariesPopup}
                    />
                </PopupOld>
            )}
        </>
    );
});

export default XeroAirwallexBatchPaymentFormContent;
